import { Component, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit, inject, signal, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ExecutionPlanService } from '../../shared/services/execution-plan/execution-plan.service';
import { RoutineDeleteService, routineDeleteErrorMessage } from '../../shared/services/execution-plan/routine-delete.service';
import { ProjectService } from '../../shared/services/project/project.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { VersionService } from '../../shared/services/version/version.service';
import { MessageService } from '../../shared/services/message/message.service';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import {
  ExecPlan,
  CreatePlanRequest,
  UpdatePlanRequest,
  TriggerRoutineRequest,
  JobSource
} from '../../shared/interfaces';
import { ProjectUser, FullProject, ProjectVersion } from '@app/shared/interfaces';
import { VersionDialogData } from '../../layout/project-header/version-dialog/version-dialog.component';
import { CRON_SCHEDULE_OPTIONS, CronScheduleOption } from '../../shared/constants/cron-schedule-options';
import { CronValidationUtils } from '../../shared/utils/cron-validation.utils';
import { ErrorHandlingUtils } from '../../shared/utils/error-handling.utils';
import { RoutineArgumentsUtils } from '../../shared/utils/routine-arguments.utils';
import { DisplayUtils } from '../../shared/utils/display.utils';
import { RELOAD_VERSIONS } from '../../shared/constants/general.constants';
import { ServerDatePipe } from '../../shared/pipes/server-date.pipe';

interface ProjectNotebook {
  workflowId: number;
  name: string;
  headlessOnly: boolean;
}


@Component({
  selector: 'app-plans',
  standalone: true,
  imports: [
    CommonModule,
    ServerDatePipe,
    ReactiveFormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSortModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    MatTooltipModule
  ],
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlansComponent implements OnInit, AfterViewInit {
  private executionPlanService = inject(ExecutionPlanService);
  private routineDeleteService = inject(RoutineDeleteService);
  private projectService = inject(ProjectService);
  appStateService = inject(AppStateService);
  private versionService = inject(VersionService);
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  @Input() projectId: number | null = null;
  @Input() currentUserId: number = 0;
  @Input() currentVersion: any = null; // Allow parent to pass current version
  @Input() canCreatePlans: boolean = false; // Permission to create plans
  @Output() addPlan = new EventEmitter<void>();
  @Output() editPlan = new EventEmitter<ExecPlan>();
  @Output() deletePlan = new EventEmitter<ExecPlan>();

  dataSource = new MatTableDataSource<ExecPlan>([]);
  /** Pretty-printed stored arguments per routineId, precomputed on load (SCRUM-625). */
  private routineArgumentsPretty = new Map<number, string>();
  displayedColumns: string[] = ['name', 'description', 'runsAsId', 'version', 'crontab', 'paused', 'createdAt', 'actions'];
  loading = signal(false);
  projectUsers = signal<ProjectUser[]>([]);
  projectNotebooks = signal<ProjectNotebook[]>([]);
  projectVersions = signal<ProjectVersion[]>([]);
  currentProject = signal<FullProject | null>(null);

  // Add form properties
  showAddForm = signal(false);
  addForm: FormGroup;
  addFormLoading = signal(false);
  addFormError = signal<string | null>(null);

  // Edit form properties
  showEditForm = signal(false);
  editForm: FormGroup;
  editFormLoading = signal(false);
  editFormError = signal<string | null>(null);
  editingPlan = signal<ExecPlan | null>(null);

  // Cron schedule options (shared constant)
  cronScheduleOptions: CronScheduleOption[] = CRON_SCHEDULE_OPTIONS;

  showCustomCronInput = signal(false);

  // Version selection properties
  selectedVersionId = signal<number | null>(null);
  showVersionSelector = signal(false);

  @ViewChild(MatSort) sort!: MatSort;

  constructor() {
    this.addForm = this.createPlanForm();
    this.editForm = this.createPlanForm();
  }

  ngOnInit(): void {
    this.loadProjectInfo();
    this.subscribeToVersionUpdates();
  }

  private loadProjectInfo(): void {
    if (!this.projectId) return;

    this.loading.set(true);

    // Load project info first
    this.projectService.getProject(this.projectId).subscribe({
      next: (project) => {
        this.currentProject.set(project);
        this.projectVersions.set(project.versions || []);

        // Check if we have a selected version already
        if (this.selectedVersionId()) {
          // Version already selected, load data for it
          this.showVersionSelector.set(false);
          this.loadPlans();
          this.loadProjectUsers();
          this.loadProjectNotebooks();
          this.loading.set(false);
          return;
        }

        // Try to use current version from parent or app state
        const currentVersion = this.currentVersion || this.appStateService.version();
        if (currentVersion && this.projectVersions().some(v => v.versionId === currentVersion.versionId)) {
          // Current version exists in project versions, use it
          this.selectedVersionId.set(currentVersion.versionId);
          this.showVersionSelector.set(false);
          this.loadPlans();
          this.loadProjectUsers();
          this.loadProjectNotebooks();
          this.loading.set(false);
          return;
        }

        // No version selected and no current version available, show version selector
        this.showVersionSelector.set(true);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading project info:', error);
        this.addFormError.set('Failed to load project information.');
        this.loading.set(false);
      }
    });
  }

  private loadPlans(): void {
    if (!this.projectId) return;

    this.executionPlanService.getPlans(this.projectId).subscribe({
      next: (plans) => {
        // Precompute pretty-printed arguments — the template helpers run on
        // every change-detection pass, so don't JSON.parse per call.
        this.routineArgumentsPretty = new Map(
          plans
            .map((plan): [number, string] => [
              plan.routineId,
              RoutineArgumentsUtils.pretty(plan.arguments),
            ])
            .filter(([, pretty]) => pretty !== ''),
        );
        this.dataSource.data = plans;
      },
      error: (error) => {
        console.error('Error loading plans:', error);
      }
    });
  }

  private loadProjectUsers(): void {
    if (!this.projectId) return;

    this.projectService.getProjectUsers(this.projectId).subscribe({
      next: (users) => {
        this.projectUsers.set(users);
      },
      error: (error) => {
        console.error('Error loading project users:', error);
      }
    });
  }

  private loadProjectNotebooks(): void {
    if (!this.projectId) return;

    // Use the selected version or current version
    const versionId = this.selectedVersionId() || this.currentVersion?.versionId || this.appStateService.version()?.versionId;
    if (!versionId) return;

    this.projectService.getFullVersion(this.projectId, versionId).subscribe({
      next: (version) => {
        if (version.workflows) {
          this.projectNotebooks.set(version.workflows
            .filter(workflow => workflow.workflowId !== undefined && workflow.name !== undefined)
            .map(workflow => ({
              workflowId: workflow.workflowId,
              name: workflow.name,
              headlessOnly: workflow.headlessOnly ?? false
            })));
        }
      },
      error: (error) => {
        console.error('Error loading project notebooks:', error);
      }
    });
  }

  // Get current version display name for the table
  getCurrentVersionDisplayName(): string {
    // First try to get the selected version from the list
    if (this.selectedVersionId()) {
      const selectedVersion = this.projectVersions().find(v => v.versionId === this.selectedVersionId());
      if (selectedVersion) {
        return `v${selectedVersion.majorVersion}.${selectedVersion.minorVersion}`;
      }
    }
    
    // Fall back to current version or app state version
    const currentVersion = this.currentVersion || this.appStateService.version();
    if (!currentVersion) return 'No version';
    return `v${currentVersion.majorVersion}.${currentVersion.minorVersion}`;
  }

  // Handle version selection
  onVersionSelected(versionId: number): void {
    this.selectedVersionId.set(versionId);
    this.showVersionSelector.set(false);

    // Load data for the selected version
    this.loadPlans();
    this.loadProjectUsers();
    this.loadProjectNotebooks();
  }

  // Allow user to change version selection
  changeVersion(): void {
    this.showVersionSelector.set(true);
    this.closeAllForms(); // Close any open forms when changing version
  }

  // Subscribe to version updates from the version dialog
  private subscribeToVersionUpdates(): void {
    this.messageService.getMessage().subscribe((message: any) => {
      if (message.type === RELOAD_VERSIONS && this.projectId) {
        // Reload project info to get the updated versions
        this.loadProjectInfo();
      }
    });
  }

  // Open version dialog to create a new version
  openVersionDialog(): void {
    if (!this.projectId) return;

    const dialogData: VersionDialogData = {
      projectId: this.projectId
    };

    this.dialogService.versionDialog(dialogData).subscribe(result => {
      if (result) {
        // Version was created successfully
        console.log('Version created from plans component:', result);
        // The RELOAD_VERSIONS message will trigger loadProjectInfo()
      }
    });
  }

  // Create a new version (kept for backward compatibility, but now opens dialog)
  createNewVersion(_majorVersion: number, _minorVersion: number): void {
    // Open the version dialog instead of creating directly
    this.openVersionDialog();
  }

  // Get version display name
  getVersionDisplayName(version: ProjectVersion): string {
    return `v${version.majorVersion}.${version.minorVersion}`;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  getUserName(userId: number): string {
    const user = this.projectUsers().find(u => u.userId === userId);
    return user?.userName || 'Unknown User';
  }

  private createPlanForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      runsAsId: [null, [Validators.required]],
      versionId: [{ value: null, disabled: true }, [Validators.required]], // Added version selection
      workflowId: [null],
      crontab: [''],
      cronScheduleType: [''] // New field for schedule type selection
    });
  }

  onAddNew(): void {
    // Check if user has permission to create plans
    if (!this.canCreatePlans) {
      this.addFormError.set('You do not have permission to create plans. Only project owners and administrators can create plans.');
      return;
    }

    // Check if there are any versions available
    if (this.projectVersions().length === 0) {
      this.addFormError.set('No versions available. Please create a version before creating plans.');
      return;
    }

    // Check if a version has been selected
    if (!this.selectedVersionId()) {
      this.addFormError.set('Please select a version first before creating plans.');
      return;
    }

    this.showAddForm.set(true);
    this.showEditForm.set(false);
    this.resetForm(this.addForm);
    this.addFormError.set(null);
    this.showCustomCronInput.set(false);
  }

  private closeAllForms(): void {
    this.showAddForm.set(false);
    this.showEditForm.set(false);
    this.editingPlan.set(null);
    this.addFormError.set(null);
    this.editFormError.set(null);
    this.showCustomCronInput.set(false);
  }

  private resetForm(form: FormGroup): void {
    form.reset();

    // Set default user if available, or use current user
    const defaultUserId = this.currentUserId || (this.projectUsers().length > 0 ? this.projectUsers()[0].userId : null);

    // Always use the selected version (user must select version before creating plans)
    const versionId = this.selectedVersionId();
    
    if (defaultUserId || versionId) {
      form.patchValue({
        runsAsId: defaultUserId,
        versionId: versionId,
        cronScheduleType: '' // Reset to manual
      });
    }
    
    // Load notebooks for the selected version
    if (versionId) {
      this.loadProjectNotebooks();
    }
  }

  onCancelAdd(): void {
    this.closeAllForms();
  }

  // Handle version selection change in the form - loads notebooks for the selected version
  onVersionChange(form: FormGroup, versionId: number): void {
    if (!this.projectId) return;

    // Load notebooks for the selected version
    this.projectService.getFullVersion(this.projectId, versionId).subscribe({
      next: (version) => {
        if (version.workflows) {
          this.projectNotebooks.set(version.workflows
            .filter(workflow => workflow.workflowId !== undefined && workflow.name !== undefined)
            .map(workflow => ({
              workflowId: workflow.workflowId,
              name: workflow.name,
              headlessOnly: workflow.headlessOnly ?? false
            })));
        } else {
          this.projectNotebooks.set([]);
        }

        // Reset workflow selection when version changes
        form.patchValue({ workflowId: null });
      },
      error: (error) => {
        console.error('Error loading notebooks for version:', error);
        this.projectNotebooks.set([]);
        form.patchValue({ workflowId: null });
      }
    });
  }

  // Handle notebook selection change
  onNotebookSelectionChange(_form: FormGroup, _event: any): void {
    // Notebook selection is now handled automatically by the dropdown
    // No additional logic needed since we only use workflowId
  }

  // Handle cron schedule selection
  onCronScheduleChange(form: FormGroup, event: any): void {
    const selectedValue = event.value;

    if (selectedValue === 'custom') {
      this.showCustomCronInput.set(true);
      form.patchValue({ crontab: '' });
    } else {
      this.showCustomCronInput.set(false);
      form.patchValue({ crontab: selectedValue });
    }
  }

  // Validate custom cron expression
  validateCustomCron(crontab: string): boolean {
    return CronValidationUtils.validateCustomCron(crontab, this.executionPlanService);
  }

  // Get validation error message for custom cron
  getCronValidationError(crontab: string): string | null {
    return CronValidationUtils.getCronValidationError(crontab, this.executionPlanService);
  }

  // Get cron format examples for help
  getCronFormatExamples(): { label: string; example: string; description: string }[] {
    return CronValidationUtils.getCronFormatExamples();
  }

  // Get the selected cron schedule label
  getSelectedCronScheduleLabel(form: FormGroup): string {
    const crontab = form.get('crontab')?.value;
    if (!crontab) return 'Manual only (no schedule)';

    const option = this.cronScheduleOptions.find(opt => opt.value === crontab);
    return option ? option.label : 'Custom schedule';
  }

  private isHeadVersionId(versionId: number | null | undefined): boolean {
    if (!versionId) return false;
    const version = this.projectVersions().find(v => v.versionId === versionId);
    return version !== undefined && DisplayUtils.isHeadVersion(version);
  }

  private createPlanRequestFromForm(formValue: any): CreatePlanRequest {
    // Determine source based on whether crontab is provided
    const crontab = formValue.crontab?.trim();
    const hasCrontab = !!crontab;
    const source = hasCrontab ? JobSource.Scheduled : JobSource.Manual;

    // When the selected version is the HEAD version (majorVersion=0, minorVersion=0),
    // send useHead: true instead of versionId — the backend rejects HEAD version IDs
    // in the versionId field with "plans require a version to have been created" (SCRUM-518).
    const useHead = this.isHeadVersionId(formValue.versionId);

    const request: CreatePlanRequest = {
      name: formValue.name,
      description: formValue.description || '',
      runsAsId: formValue.runsAsId,
      source: source,
      workflowId: formValue.workflowId || undefined
    };

    if (hasCrontab) {
      request.crontab = crontab;
    }

    if (useHead) {
      request.useHead = true;
    } else {
      request.versionId = formValue.versionId || undefined;
    }

    return request;
  }

  private updatePlanRequestFromForm(formValue: any): UpdatePlanRequest {
    // Note: Version cannot be changed when updating a plan
    // Determine source based on whether crontab is provided
    const crontab = formValue.crontab?.trim();
    const hasCrontab = !!crontab;
    const source = hasCrontab ? JobSource.Scheduled : JobSource.Manual;

    const request: UpdatePlanRequest = {
      name: formValue.name,
      description: formValue.description || undefined,
      source: source,
      workflowId: formValue.workflowId || undefined
    };

    if (hasCrontab) {
      request.crontab = crontab;
    }

    return request;
  }

  onSaveAdd(): void {
    const validationResult = this.validateAddFormData();
    if (!validationResult.valid) {
      this.addFormError.set(validationResult.error!);
      return;
    }

    this.executePlanCreation();
  }

  /**
   * Validate add form data
   */
  private validateAddFormData(): { valid: boolean; error?: string } {
    if (this.addForm.invalid || !this.projectId) {
      return { valid: false, error: 'Form is invalid or project ID is missing' };
    }

    if (!this.addForm.get('versionId')?.value) {
      return { 
        valid: false, 
        error: 'No version selected. Please select a version before creating plans.' 
      };
    }

    // HEAD version (majorVersion=0, minorVersion=0) does not support scheduled routines.
    // Block early with a clear message rather than letting the backend return a cryptic error.
    const selectedVersionId = this.addForm.get('versionId')?.value;
    const crontab = this.addForm.get('crontab')?.value?.trim();
    const hasCrontab = !!crontab;
    if (hasCrontab && this.isHeadVersionId(selectedVersionId)) {
      return {
        valid: false,
        error: 'Scheduled routines require a published version. Please select a published version or set the schedule to "Manual only (no schedule)".'
      };
    }

    if (hasCrontab && !this.validateCustomCron(crontab)) {
      return { 
        valid: false, 
        error: this.getCronValidationError(crontab) || 
               'Invalid cron expression format. Please use the format: minute hour day month weekday (e.g., "0 2 * * *")' 
      };
    }

    return { valid: true };
  }

  /**
   * Execute plan creation
   */
  private executePlanCreation(): void {
    this.addFormLoading.set(true);
    this.addFormError.set(null);

    try {
      const planRequest = this.createPlanRequestFromForm(this.addForm.getRawValue());
      this.createPlan(planRequest);
    } catch (error: any) {
      this.addFormLoading.set(false);
      this.addFormError.set(error.message || 'Failed to create plan request');
    }
  }

  /**
   * Create plan via service
   */
  private createPlan(planRequest: CreatePlanRequest): void {
    this.executionPlanService.createPlan(this.projectId!, planRequest).subscribe({
      next: () => this.handlePlanCreated(),
      error: (error) => this.handlePlanCreationError(error)
    });
  }

  /**
   * Handle successful plan creation
   */
  private handlePlanCreated(): void {
    this.addFormLoading.set(false);
    this.closeAllForms();
    this.loadPlans();
    this.addPlan.emit();
  }

  /**
   * Handle plan creation error
   */
  private handlePlanCreationError(error: any): void {
    this.addFormLoading.set(false);
    this.addFormError.set(ErrorHandlingUtils.getErrorMessage(error));
  }


  getFormFieldError(form: FormGroup, fieldName: string): string | null {
    const field = form.get(fieldName);
    if (field && field.invalid && (field.dirty || field.touched)) {
      if (field.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors?.['maxlength']) {
        return `${this.getFieldLabel(fieldName)} is too long`;
      }
    }
    return null;
  }

  getAddFormFieldError(fieldName: string): string | null {
    return this.getFormFieldError(this.addForm, fieldName);
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Plan Name',
      description: 'Description',
      runsAsId: 'Runs As User',
      versionId: 'Version',
      workflowId: 'Notebook',
      crontab: 'Cron Schedule'
    };
    return labels[fieldName] || fieldName;
  }

  onEdit(plan: ExecPlan): void {
    this.editingPlan.set(plan);
    this.showEditForm.set(true);
    this.showAddForm.set(false);
    this.editFormError.set(null);
    this.showCustomCronInput.set(false);

    // Determine if the crontab matches a predefined option
    const matchingOption = this.cronScheduleOptions.find(opt => opt.value === plan.crontab);
    const cronScheduleType = matchingOption ? plan.crontab : 'custom';

    this.editForm.patchValue({
      name: plan.name,
      description: plan.description || '',
      runsAsId: plan.runsAsId,
      versionId: plan.versionId, // Show the plan's version (read-only in edit mode)
      workflowId: plan.workflowId || null,
      crontab: plan.crontab || '',
      cronScheduleType: cronScheduleType
    });

    // Show custom input if it's a custom crontab
    if (cronScheduleType === 'custom' && plan.crontab) {
      this.showCustomCronInput.set(true);
    }

    // Load notebooks for the plan's version
    if (plan.versionId) {
      this.onVersionChange(this.editForm, plan.versionId);
    }
  }

  onCancelEdit(): void {
    this.closeAllForms();
  }

  onSaveEdit(): void {
    if (this.editForm.invalid || !this.projectId || !this.editingPlan()) return;

    // Validate custom cron expression if provided
    const crontab = this.editForm.get('crontab')?.value;
    if (crontab && !this.validateCustomCron(crontab)) {
      this.editFormError.set(this.getCronValidationError(crontab) || 'Invalid cron expression format. Please use the format: minute hour day month weekday (e.g., "0 2 * * *")');
      return;
    }

    this.editFormLoading.set(true);
    this.editFormError.set(null);

    try {
      const updateRequest = this.updatePlanRequestFromForm(this.editForm.getRawValue());

      this.executionPlanService.updatePlan(this.projectId, this.editingPlan()!.routineId, updateRequest).subscribe({
        next: (response) => {
          this.editFormLoading.set(false);
          this.closeAllForms();
          this.loadPlans(); // Reload to get the latest data
          this.editPlan.emit(response.routine);
        },
        error: (error) => {
          this.editFormLoading.set(false);
          this.editFormError.set(ErrorHandlingUtils.getErrorMessage(error));
        }
      });
    } catch (error: any) {
      this.editFormLoading.set(false);
      this.editFormError.set(error.message || 'Failed to update plan request');
    }
  }

  getEditFormFieldError(fieldName: string): string | null {
    return this.getFormFieldError(this.editForm, fieldName);
  }

  onDelete(plan: ExecPlan): void {
    if (!this.projectId) return;
    this.routineDeleteService
      .deleteRoutine(this.projectId, plan.routineId, plan.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loadPlans(); // Reload the list
          this.deletePlan.emit(plan);
        },
        error: (error) => {
          console.error('Error deleting routine:', error);
          this.messageService.showMessage('error', routineDeleteErrorMessage(error));
        }
      });
  }

  onActionMenu(plan: ExecPlan, action: 'run' | 'delete'): void {
    switch (action) {
      case 'run':
        this.runPlan(plan);
        break;
      case 'delete':
        this.onDelete(plan);
        break;
      default: {
        const exhaustive: never = action;
        console.error('Unhandled routine action:', exhaustive);
      }
    }
  }

  private runPlan(plan: ExecPlan): void {
    if (!this.projectId) return;
    const projectId = this.projectId;

    // Fetch workflow to get variable_schema before opening the dialog (mirrors sidebar behaviour).
    if (plan.workflowId && plan.versionId) {
      this.executionPlanService.getWorkflow(projectId, plan.versionId, plan.workflowId)
        .subscribe({
          next: (workflow) => this.openRunDialog(plan, projectId, workflow?.variableSchema),
          error: () => this.openRunDialog(plan, projectId, undefined),
        });
    } else if (plan.workflowName) {
      const headVersionId = this.appStateService.headVersionId();
      if (headVersionId) {
        this.executionPlanService.getWorkflowByName(projectId, headVersionId, plan.workflowName)
          .subscribe({
            next: (workflow) => this.openRunDialog(plan, projectId, workflow?.variableSchema),
            error: () => this.openRunDialog(plan, projectId, undefined),
          });
      } else {
        this.openRunDialog(plan, projectId, undefined);
      }
    } else {
      this.openRunDialog(plan, projectId, undefined);
    }
  }

  private openRunDialog(plan: ExecPlan, projectId: number, variableSchema?: unknown): void {
    this.dialogService.runNowDialog({
      routineName: plan.name,
      useHeadDefault: !!plan.useHead,
      variableSchema,
      workflowName: plan.workflowName,
      routineArguments: RoutineArgumentsUtils.parse(plan.arguments),
    }).subscribe(result => {
      if (!result) return;

      const body: TriggerRoutineRequest = {
        runs_as_id: this.currentUserId || plan.runsAsId,
        // Send explicit boolean so that unchecking HEAD in the run dialog
        // correctly overrides a HEAD routine's default (use_head=true).
        use_head: result.useHead,
        arguments: result.arguments,
      };

      this.executionPlanService.triggerPlan(projectId, plan.routineId, body).subscribe({
        next: () => {
          this.messageService.showMessage('success', 'Routine triggered successfully');
          this.loadPlans();
        },
        error: (error) => {
          console.error('Error running plan:', error);
          const msg = ErrorHandlingUtils.getErrorMessage(error) || 'Failed to trigger routine';
          this.messageService.showMessage('error', msg);
        }
      });
    });
  }

  /** Whether a routine has non-empty stored arguments to display (SCRUM-625). */
  hasRoutineArguments(plan: ExecPlan): boolean {
    return this.routineArgumentsPretty.has(plan.routineId);
  }

  /** Pretty-printed stored arguments for the list tooltip. */
  formatRoutineArguments(plan: ExecPlan): string {
    return this.routineArgumentsPretty.get(plan.routineId) ?? '';
  }

  formatCronExpression(crontab: string): string {
    const commonName = this.findCommonCronName(crontab);
    if (commonName) {
      return commonName;
    }

    const customFormat = this.formatCustomCronExpression(crontab);
    return customFormat || crontab;
  }

  /**
   * Check if crontab matches a common expression from the service
   */
  private findCommonCronName(crontab: string): string | null {
    const commonExpressions = this.executionPlanService.getCommonCronExpressions();
    for (const [name, expr] of Object.entries(commonExpressions)) {
      if (expr === crontab) {
        return name;
      }
    }
    return null;
  }

  /**
   * Format custom cron expressions into human-readable strings
   */
  private formatCustomCronExpression(crontab: string): string | null {
    const parts = this.parseCronExpression(crontab);
    if (!parts) {
      return null;
    }

    return this.matchCronPattern(parts);
  }

  /**
   * Parse cron expression into parts
   */
  private parseCronExpression(crontab: string): string[] | null {
    const parts = crontab.split(' ');
    return parts.length >= 5 ? parts : null;
  }

  /**
   * Match cron parts against known patterns
   */
  private matchCronPattern(parts: string[]): string | null {
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    const cronParts = { minute, hour, dayOfMonth, month, dayOfWeek };

    const patterns = [
      { check: () => this.isDailyAtMidnight(cronParts), label: 'Daily at midnight' },
      { check: () => this.isDailyAtHour(cronParts), label: `Daily at ${hour}:00` },
      { check: () => this.isWeeklyOnSunday(cronParts), label: 'Weekly on Sunday' },
      { check: () => this.isMonthlyOnFirst(cronParts), label: 'Monthly on 1st' }
    ];

    for (const pattern of patterns) {
      if (pattern.check()) {
        return pattern.label;
      }
    }

    return null;
  }

  /**
   * Check if cron represents daily at midnight
   */
  private isDailyAtMidnight(parts: any): boolean {
    return parts.minute === '0' && parts.hour === '0' && 
           parts.dayOfMonth === '*' && parts.month === '*' && parts.dayOfWeek === '*';
  }

  /**
   * Check if cron represents daily at specific hour
   */
  private isDailyAtHour(parts: any): boolean {
    return parts.minute === '0' && parts.dayOfMonth === '*' && 
           parts.month === '*' && parts.dayOfWeek === '*';
  }

  /**
   * Check if cron represents weekly on Sunday
   */
  private isWeeklyOnSunday(parts: any): boolean {
    return parts.minute === '0' && parts.hour === '0' && 
           parts.dayOfMonth === '*' && parts.month === '*' && parts.dayOfWeek === '0';
  }

  /**
   * Check if cron represents monthly on first day
   */
  private isMonthlyOnFirst(parts: any): boolean {
    return parts.minute === '0' && parts.hour === '0' && 
           parts.dayOfMonth === '1' && parts.month === '*' && parts.dayOfWeek === '*';
  }
}
